import ExcelJS from "exceljs";
import { Agent } from "@/types/agent/agent";
import { TestRun } from "@/types/agent/brand-integrity";

// Types for the analysis result structure from the edge function
interface ConversationTurn {
  role: "user" | "agent";
  message: string;
  time_in_call_secs?: number;
}

interface EvaluationCriteriaResult {
  result: "success" | "failure" | "unknown";
  reasoning: string;
}

interface ElevenLabsAnalysis {
  call_successful: "success" | "failure" | "unknown";
  transcript_summary: string;
  evaluation_criteria_results: Record<string, EvaluationCriteriaResult>;
}

interface AnalysisResult {
  category_id: string;
  category_name: string;
  test_type: "core" | "suggested" | "custom" | "manual";
  conversation_turns: number;
  passed: boolean;
  elevenlabs_analysis?: ElevenLabsAnalysis;
  transcript?: ConversationTurn[];
  criteria_lookup?: Record<string, { name: string; description: string }>;
  duration_seconds?: number;
  elevenlabs_conversation_id?: string;
  error?: string;
}

interface Issue {
  type: string;
  severity: "high" | "medium" | "low";
  message: string;
}

interface TestResultWithAnalysis {
  id: number;
  test_run_id: number;
  agent_id: string;
  scenario_id: number | null;
  test_prompt: string;
  agent_response: string;
  analysis_result: AnalysisResult | null;
  brand_score: number | null;
  issues_found: Issue[] | null;
  created_at: string;
}

interface TestRunWithResults extends TestRun {
  results: TestResultWithAnalysis[];
}

export interface BrandIntegrityExportOptions {
  includeTranscripts: boolean;
  includeCriteriaDetails: boolean;
  dateRange?: string;
}

const DEFAULT_OPTIONS: BrandIntegrityExportOptions = {
  includeTranscripts: true,
  includeCriteriaDetails: true,
};

// Brand colors
const COLORS = {
  primary: "4E9CFF",
  primaryLight: "E8F2FF",
  accent: "FF4DB8",
  success: "10B981",
  successLight: "D1FAE5",
  warning: "F59E0B",
  warningLight: "FEF3C7",
  error: "EF4444",
  errorLight: "FEE2E2",
  headerBg: "1F2937",
  headerText: "FFFFFF",
  altRowBg: "F9FAFB",
  borderColor: "E5E7EB",
};

export class BrandIntegrityExcelExport {
  private workbook: ExcelJS.Workbook;
  private agent: Agent;
  private testRuns: TestRunWithResults[];
  private options: BrandIntegrityExportOptions;

  constructor(
    agent: Agent,
    testRuns: TestRunWithResults[],
    options: Partial<BrandIntegrityExportOptions> = {}
  ) {
    this.workbook = new ExcelJS.Workbook();
    this.agent = agent;
    this.testRuns = testRuns;
    this.options = { ...DEFAULT_OPTIONS, ...options };

    // Set workbook properties
    this.workbook.creator = "Help Genie";
    this.workbook.created = new Date();
    this.workbook.modified = new Date();
  }

  async generate(): Promise<Blob> {
    // Create all sheets
    this.createExecutiveSummarySheet();
    this.createTestRunsSheet();
    this.createDetailedResultsSheet();

    if (this.options.includeTranscripts) {
      this.createTranscriptsSheet();
    }

    this.createIssuesAnalysisSheet();

    if (this.options.includeCriteriaDetails) {
      this.createCriteriaBreakdownSheet();
    }

    // Generate buffer and convert to Blob
    const buffer = await this.workbook.xlsx.writeBuffer();
    return new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  }

  private getAllResults(): TestResultWithAnalysis[] {
    return this.testRuns.flatMap((run) => run.results || []);
  }

  private calculateStats() {
    const allResults = this.getAllResults();
    const passedResults = allResults.filter((r) => r.analysis_result?.passed === true);
    const failedResults = allResults.filter((r) => r.analysis_result?.passed === false);
    const allIssues = allResults.flatMap((r) => r.issues_found || []);

    const scores = allResults
      .map((r) => r.brand_score)
      .filter((s): s is number => s !== null && s !== undefined);

    const avgScore = scores.length > 0
      ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
      : 0;

    return {
      totalTestRuns: this.testRuns.length,
      totalTests: allResults.length,
      passed: passedResults.length,
      failed: failedResults.length,
      passRate: allResults.length > 0
        ? Math.round((passedResults.length / allResults.length) * 100)
        : 0,
      avgScore,
      totalIssues: allIssues.length,
      highSeverityIssues: allIssues.filter((i) => i.severity === "high").length,
      mediumSeverityIssues: allIssues.filter((i) => i.severity === "medium").length,
      lowSeverityIssues: allIssues.filter((i) => i.severity === "low").length,
    };
  }

  private applyHeaderStyle(row: ExcelJS.Row) {
    row.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: COLORS.headerBg },
      };
      cell.font = {
        bold: true,
        color: { argb: COLORS.headerText },
        size: 11,
      };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      cell.border = {
        bottom: { style: "medium", color: { argb: COLORS.primary } },
      };
    });
    row.height = 24;
  }

  private applyDataRowStyle(row: ExcelJS.Row, isAltRow: boolean) {
    row.eachCell((cell) => {
      if (isAltRow) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: COLORS.altRowBg },
        };
      }
      cell.alignment = { vertical: "middle", wrapText: true };
      cell.border = {
        bottom: { style: "thin", color: { argb: COLORS.borderColor } },
      };
    });
  }

  private createExecutiveSummarySheet() {
    const sheet = this.workbook.addWorksheet("Executive Summary", {
      properties: { tabColor: { argb: COLORS.primary } },
    });

    const stats = this.calculateStats();

    // Title section
    sheet.mergeCells("A1:F1");
    const titleCell = sheet.getCell("A1");
    titleCell.value = "Genie Trust Center Test Report";
    titleCell.font = { bold: true, size: 24, color: { argb: COLORS.primary } };
    titleCell.alignment = { horizontal: "center" };
    sheet.getRow(1).height = 40;

    // Subtitle
    sheet.mergeCells("A2:F2");
    const subtitleCell = sheet.getCell("A2");
    subtitleCell.value = `Genie: ${this.agent.name}`;
    subtitleCell.font = { size: 14, color: { argb: "666666" } };
    subtitleCell.alignment = { horizontal: "center" };

    // Generated date
    sheet.mergeCells("A3:F3");
    const dateCell = sheet.getCell("A3");
    dateCell.value = `Generated: ${new Date().toLocaleString()}`;
    dateCell.font = { size: 11, color: { argb: "999999" } };
    dateCell.alignment = { horizontal: "center" };

    // Spacer
    sheet.getRow(4).height = 20;

    // KPI Cards section
    sheet.mergeCells("A5:F5");
    const kpiTitleCell = sheet.getCell("A5");
    kpiTitleCell.value = "Key Performance Indicators";
    kpiTitleCell.font = { bold: true, size: 14, color: { argb: COLORS.headerBg } };
    kpiTitleCell.border = { bottom: { style: "medium", color: { argb: COLORS.primary } } };

    // KPI data
    const kpis = [
      { label: "Overall Score", value: `${stats.avgScore}%`, color: this.getScoreColor(stats.avgScore) },
      { label: "Pass Rate", value: `${stats.passRate}%`, color: this.getScoreColor(stats.passRate) },
      { label: "Total Test Runs", value: stats.totalTestRuns.toString(), color: COLORS.primary },
      { label: "Total Tests", value: stats.totalTests.toString(), color: COLORS.primary },
      { label: "Tests Passed", value: stats.passed.toString(), color: COLORS.success },
      { label: "Tests Failed", value: stats.failed.toString(), color: stats.failed > 0 ? COLORS.error : COLORS.success },
    ];

    let row = 7;
    kpis.forEach((kpi, idx) => {
      const col = (idx % 3) * 2 + 1;
      if (idx > 0 && idx % 3 === 0) row += 3;

      const labelCell = sheet.getCell(row, col);
      labelCell.value = kpi.label;
      labelCell.font = { size: 10, color: { argb: "666666" } };

      const valueCell = sheet.getCell(row + 1, col);
      valueCell.value = kpi.value;
      valueCell.font = { bold: true, size: 20, color: { argb: kpi.color } };
    });

    // Issues Summary section
    row += 5;
    sheet.mergeCells(`A${row}:F${row}`);
    const issuesTitleCell = sheet.getCell(`A${row}`);
    issuesTitleCell.value = "Issues Summary";
    issuesTitleCell.font = { bold: true, size: 14, color: { argb: COLORS.headerBg } };
    issuesTitleCell.border = { bottom: { style: "medium", color: { argb: COLORS.accent } } };

    row += 2;
    const issuesSummary = [
      { label: "Total Issues", value: stats.totalIssues, color: COLORS.headerBg },
      { label: "High Severity", value: stats.highSeverityIssues, color: COLORS.error },
      { label: "Medium Severity", value: stats.mediumSeverityIssues, color: COLORS.warning },
      { label: "Low Severity", value: stats.lowSeverityIssues, color: COLORS.success },
    ];

    issuesSummary.forEach((item, idx) => {
      const col = idx + 1;
      const labelCell = sheet.getCell(row, col);
      labelCell.value = item.label;
      labelCell.font = { size: 10, color: { argb: "666666" } };

      const valueCell = sheet.getCell(row + 1, col);
      valueCell.value = item.value;
      valueCell.font = { bold: true, size: 18, color: { argb: item.color } };
    });

    // Test Category Breakdown
    row += 5;
    sheet.mergeCells(`A${row}:F${row}`);
    const categoryTitleCell = sheet.getCell(`A${row}`);
    categoryTitleCell.value = "Performance by Test Category";
    categoryTitleCell.font = { bold: true, size: 14, color: { argb: COLORS.headerBg } };
    categoryTitleCell.border = { bottom: { style: "medium", color: { argb: COLORS.primary } } };

    row += 2;
    const categoryHeaders = ["Category", "Tests", "Passed", "Failed", "Pass Rate", "Avg Score"];
    categoryHeaders.forEach((header, idx) => {
      const cell = sheet.getCell(row, idx + 1);
      cell.value = header;
      cell.font = { bold: true, size: 10, color: { argb: COLORS.headerText } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.headerBg } };
      cell.alignment = { horizontal: "center" };
    });

    // Group results by category
    const allResults = this.getAllResults();
    const categoryMap = new Map<string, TestResultWithAnalysis[]>();

    allResults.forEach((result) => {
      const category = result.analysis_result?.category_name || "Unknown";
      if (!categoryMap.has(category)) {
        categoryMap.set(category, []);
      }
      categoryMap.get(category)!.push(result);
    });

    row++;
    categoryMap.forEach((results, category) => {
      const passed = results.filter((r) => r.analysis_result?.passed).length;
      const failed = results.length - passed;
      const passRate = Math.round((passed / results.length) * 100);
      const scores = results.map((r) => r.brand_score).filter((s): s is number => s !== null);
      const avgScore = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;

      const rowData = [category, results.length, passed, failed, `${passRate}%`, `${avgScore}%`];
      rowData.forEach((value, idx) => {
        const cell = sheet.getCell(row, idx + 1);
        cell.value = value;
        cell.alignment = { horizontal: idx === 0 ? "left" : "center" };

        // Color coding for pass rate and avg score
        if (idx === 4 || idx === 5) {
          const numValue = parseInt(String(value));
          cell.font = { color: { argb: this.getScoreColor(numValue) } };
        }
      });
      this.applyDataRowStyle(sheet.getRow(row), row % 2 === 0);
      row++;
    });

    // Set column widths
    sheet.columns = [
      { width: 25 }, { width: 15 }, { width: 15 }, { width: 15 }, { width: 15 }, { width: 15 },
    ];
  }

  private createTestRunsSheet() {
    const sheet = this.workbook.addWorksheet("Test Runs", {
      properties: { tabColor: { argb: COLORS.primary } },
    });

    const headers = [
      "Run ID",
      "Date & Time",
      "Status",
      "Test Type",
      "Total Tests",
      "Passed",
      "Failed",
      "Pass Rate",
      "Duration",
    ];

    const headerRow = sheet.addRow(headers);
    this.applyHeaderStyle(headerRow);

    this.testRuns.forEach((run, idx) => {
      const results = run.results || [];
      const passed = results.filter((r) => r.analysis_result?.passed).length;
      const failed = results.length - passed;
      const passRate = results.length > 0 ? Math.round((passed / results.length) * 100) : 0;

      // Calculate duration
      let duration = "N/A";
      if (run.started_at && run.completed_at) {
        const start = new Date(run.started_at);
        const end = new Date(run.completed_at);
        const diffMs = end.getTime() - start.getTime();
        const diffSecs = Math.round(diffMs / 1000);
        duration = diffSecs < 60 ? `${diffSecs}s` : `${Math.round(diffSecs / 60)}m ${diffSecs % 60}s`;
      }

      const row = sheet.addRow([
        run.id,
        new Date(run.created_at).toLocaleString(),
        run.status,
        run.test_type || "automated",
        run.total_tests || results.length,
        passed,
        failed,
        `${passRate}%`,
        duration,
      ]);

      this.applyDataRowStyle(row, idx % 2 === 1);

      // Color code status
      const statusCell = row.getCell(3);
      if (run.status === "completed") {
        statusCell.font = { color: { argb: COLORS.success } };
      } else if (run.status === "failed") {
        statusCell.font = { color: { argb: COLORS.error } };
      } else if (run.status === "running") {
        statusCell.font = { color: { argb: COLORS.warning } };
      }

      // Color code pass rate
      const passRateCell = row.getCell(8);
      passRateCell.font = { color: { argb: this.getScoreColor(passRate) } };
    });

    // Auto-fit columns
    sheet.columns = [
      { width: 12 }, { width: 20 }, { width: 12 }, { width: 12 },
      { width: 12 }, { width: 10 }, { width: 10 }, { width: 12 }, { width: 12 },
    ];
  }

  private createDetailedResultsSheet() {
    const sheet = this.workbook.addWorksheet("Detailed Results", {
      properties: { tabColor: { argb: COLORS.accent } },
    });

    const headers = [
      "Run ID",
      "Test Date",
      "Category",
      "Test Type",
      "Result",
      "Score",
      "Conversation Turns",
      "Call Status",
      "Summary",
      "Issues Count",
    ];

    const headerRow = sheet.addRow(headers);
    this.applyHeaderStyle(headerRow);

    const allResults = this.getAllResults();
    allResults.forEach((result, idx) => {
      const analysis = result.analysis_result;
      const row = sheet.addRow([
        result.test_run_id,
        new Date(result.created_at || "").toLocaleString(),
        analysis?.category_name || "Unknown",
        analysis?.test_type || "unknown",
        analysis?.passed ? "PASSED" : "FAILED",
        result.brand_score !== null ? `${result.brand_score}%` : "N/A",
        analysis?.conversation_turns || 0,
        analysis?.elevenlabs_analysis?.call_successful || "unknown",
        analysis?.elevenlabs_analysis?.transcript_summary || "",
        (result.issues_found || []).length,
      ]);

      this.applyDataRowStyle(row, idx % 2 === 1);

      // Color code result
      const resultCell = row.getCell(5);
      resultCell.font = {
        bold: true,
        color: { argb: analysis?.passed ? COLORS.success : COLORS.error },
      };

      // Color code score
      const scoreCell = row.getCell(6);
      if (result.brand_score !== null) {
        scoreCell.font = { color: { argb: this.getScoreColor(result.brand_score) } };
      }

      // Color code call status
      const callStatusCell = row.getCell(8);
      const callStatus = analysis?.elevenlabs_analysis?.call_successful;
      if (callStatus === "success") {
        callStatusCell.font = { color: { argb: COLORS.success } };
      } else if (callStatus === "failure") {
        callStatusCell.font = { color: { argb: COLORS.error } };
      }
    });

    sheet.columns = [
      { width: 10 }, { width: 18 }, { width: 22 }, { width: 12 },
      { width: 10 }, { width: 10 }, { width: 18 }, { width: 12 },
      { width: 60 }, { width: 12 },
    ];
  }

  private createTranscriptsSheet() {
    const sheet = this.workbook.addWorksheet("Conversation Transcripts", {
      properties: { tabColor: { argb: "9333EA" } },
    });

    const headers = [
      "Run ID",
      "Category",
      "Turn #",
      "Speaker",
      "Message",
      "Time (sec)",
    ];

    const headerRow = sheet.addRow(headers);
    this.applyHeaderStyle(headerRow);

    let rowIdx = 0;
    const allResults = this.getAllResults();

    allResults.forEach((result) => {
      const analysis = result.analysis_result;
      const transcript = analysis?.transcript || [];

      if (transcript.length === 0) {
        // Try to parse from agent_response if transcript not in analysis
        try {
          const parsed = JSON.parse(result.agent_response || "[]");
          if (Array.isArray(parsed)) {
            transcript.push(...parsed);
          }
        } catch {
          // Ignore parse errors
        }
      }

      transcript.forEach((turn, turnIdx) => {
        const row = sheet.addRow([
          result.test_run_id,
          analysis?.category_name || "Unknown",
          turnIdx + 1,
          turn.role === "agent" ? "Genie" : "Customer",
          turn.message,
          turn.time_in_call_secs !== undefined ? turn.time_in_call_secs.toFixed(1) : "",
        ]);

        this.applyDataRowStyle(row, rowIdx % 2 === 1);

        // Color code speaker
        const speakerCell = row.getCell(4);
        speakerCell.font = {
          bold: true,
          color: { argb: turn.role === "agent" ? COLORS.primary : COLORS.accent },
        };

        rowIdx++;
      });

      // Add separator row between conversations
      if (transcript.length > 0) {
        const separatorRow = sheet.addRow(["", "", "", "", "", ""]);
        separatorRow.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: COLORS.borderColor },
          };
        });
        separatorRow.height = 5;
        rowIdx++;
      }
    });

    sheet.columns = [
      { width: 10 }, { width: 22 }, { width: 8 }, { width: 12 },
      { width: 80 }, { width: 12 },
    ];
  }

  private createIssuesAnalysisSheet() {
    const sheet = this.workbook.addWorksheet("Issues Analysis", {
      properties: { tabColor: { argb: COLORS.error } },
    });

    const headers = [
      "Run ID",
      "Category",
      "Issue Type",
      "Severity",
      "Description",
      "Test Date",
    ];

    const headerRow = sheet.addRow(headers);
    this.applyHeaderStyle(headerRow);

    let rowIdx = 0;
    const allResults = this.getAllResults();

    allResults.forEach((result) => {
      const issues = result.issues_found || [];
      const analysis = result.analysis_result;

      issues.forEach((issue) => {
        const row = sheet.addRow([
          result.test_run_id,
          analysis?.category_name || "Unknown",
          issue.type,
          issue.severity.toUpperCase(),
          issue.message,
          new Date(result.created_at || "").toLocaleString(),
        ]);

        this.applyDataRowStyle(row, rowIdx % 2 === 1);

        // Color code severity
        const severityCell = row.getCell(4);
        const severityColors: Record<string, string> = {
          HIGH: COLORS.error,
          MEDIUM: COLORS.warning,
          LOW: COLORS.success,
        };
        severityCell.font = {
          bold: true,
          color: { argb: severityColors[issue.severity.toUpperCase()] || COLORS.headerBg },
        };
        severityCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: {
            argb: issue.severity === "high" ? COLORS.errorLight
              : issue.severity === "medium" ? COLORS.warningLight
              : COLORS.successLight,
          },
        };

        rowIdx++;
      });
    });

    // Add summary at the bottom if there are issues
    const allIssues = allResults.flatMap((r) => r.issues_found || []);
    if (allIssues.length > 0) {
      sheet.addRow([]);
      sheet.addRow([]);

      const summaryTitleRow = sheet.addRow(["Issues Summary by Type"]);
      summaryTitleRow.getCell(1).font = { bold: true, size: 12 };

      // Group by type
      const issuesByType = new Map<string, number>();
      allIssues.forEach((issue) => {
        const count = issuesByType.get(issue.type) || 0;
        issuesByType.set(issue.type, count + 1);
      });

      const typeHeaderRow = sheet.addRow(["Issue Type", "Count"]);
      this.applyHeaderStyle(typeHeaderRow);

      issuesByType.forEach((count, type) => {
        sheet.addRow([type, count]);
      });
    }

    sheet.columns = [
      { width: 10 }, { width: 22 }, { width: 20 }, { width: 12 },
      { width: 60 }, { width: 18 },
    ];
  }

  private createCriteriaBreakdownSheet() {
    const sheet = this.workbook.addWorksheet("Criteria Breakdown", {
      properties: { tabColor: { argb: COLORS.success } },
    });

    const headers = [
      "Run ID",
      "Category",
      "Criteria ID",
      "Criteria Name",
      "Result",
      "Reasoning",
    ];

    const headerRow = sheet.addRow(headers);
    this.applyHeaderStyle(headerRow);

    let rowIdx = 0;
    const allResults = this.getAllResults();

    allResults.forEach((result) => {
      const analysis = result.analysis_result;
      const criteriaResults = analysis?.elevenlabs_analysis?.evaluation_criteria_results || {};
      const criteriaLookup = analysis?.criteria_lookup || {};

      Object.entries(criteriaResults).forEach(([criteriaId, criteriaResult]) => {
        const lookup = criteriaLookup[criteriaId] || { name: criteriaId, description: "" };

        const row = sheet.addRow([
          result.test_run_id,
          analysis?.category_name || "Unknown",
          criteriaId,
          lookup.name,
          criteriaResult.result.toUpperCase(),
          criteriaResult.reasoning,
        ]);

        this.applyDataRowStyle(row, rowIdx % 2 === 1);

        // Color code result
        const resultCell = row.getCell(5);
        const resultColors: Record<string, string> = {
          SUCCESS: COLORS.success,
          FAILURE: COLORS.error,
          UNKNOWN: COLORS.warning,
        };
        resultCell.font = {
          bold: true,
          color: { argb: resultColors[criteriaResult.result.toUpperCase()] || COLORS.headerBg },
        };
        resultCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: {
            argb: criteriaResult.result === "success" ? COLORS.successLight
              : criteriaResult.result === "failure" ? COLORS.errorLight
              : COLORS.warningLight,
          },
        };

        rowIdx++;
      });
    });

    // Add summary
    const allCriteria: Array<{ id: string; result: string }> = [];
    allResults.forEach((result) => {
      const criteriaResults = result.analysis_result?.elevenlabs_analysis?.evaluation_criteria_results || {};
      Object.entries(criteriaResults).forEach(([id, cr]) => {
        allCriteria.push({ id, result: cr.result });
      });
    });

    if (allCriteria.length > 0) {
      sheet.addRow([]);
      sheet.addRow([]);

      const summaryTitleRow = sheet.addRow(["Criteria Pass/Fail Summary"]);
      summaryTitleRow.getCell(1).font = { bold: true, size: 12 };

      const summaryHeaders = ["Total Criteria Evaluated", "Passed", "Failed", "Unknown"];
      const summaryHeaderRow = sheet.addRow(summaryHeaders);
      this.applyHeaderStyle(summaryHeaderRow);

      const passed = allCriteria.filter((c) => c.result === "success").length;
      const failed = allCriteria.filter((c) => c.result === "failure").length;
      const unknown = allCriteria.filter((c) => c.result === "unknown").length;

      const summaryDataRow = sheet.addRow([allCriteria.length, passed, failed, unknown]);
      summaryDataRow.getCell(2).font = { color: { argb: COLORS.success }, bold: true };
      summaryDataRow.getCell(3).font = { color: { argb: COLORS.error }, bold: true };
      summaryDataRow.getCell(4).font = { color: { argb: COLORS.warning }, bold: true };
    }

    sheet.columns = [
      { width: 10 }, { width: 22 }, { width: 20 }, { width: 25 },
      { width: 12 }, { width: 60 },
    ];
  }

  private getScoreColor(score: number): string {
    if (score >= 80) return COLORS.success;
    if (score >= 60) return COLORS.warning;
    return COLORS.error;
  }

  static downloadExcel(blob: Blob, agentName: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `brand-integrity-${agentName.toLowerCase().replace(/\s+/g, "-")}-${
      new Date().toISOString().split("T")[0]
    }.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}
